/**
 * 坐标转换 — Build Cell ↔ 世界坐标。
 * 极坐标：sector 决定角度，ring 决定半径，layer 决定高度。
 * world→polar→world 应落在渲染容限内（cell 内任意点 round-trip 到同一 cell）。
 */

import {
  sectorsOfRing,
  type BuildCellId,
  type BuildLayer,
  type SpatialMapConfig,
  type WorldPoint,
} from './types.js';

const TWO_PI = Math.PI * 2;

/**
 * cell 的中心角度（弧度）。
 * 扇区数按环推导，因此必须给出所在层与环 —— 不能只凭 sector 求角。
 */
export function sectorAngle(
  config: SpatialMapConfig,
  sector: number,
  layer: BuildLayer = 'floor',
  ring = 0,
): number {
  return (sector + 0.5) * (TWO_PI / sectorsOfRing(config, layer, ring));
}

/** cell 的中心半径（世界单位，floor/subsurface 用）。 */
export function ringRadius(config: SpatialMapConfig, ring: number): number {
  // ring 0 中心半径可由 ringRadius0 指定；否则从中心起算，每环宽 ringWidth
  return (config.ringRadius0 ?? config.ringWidth * 0.5) + ring * config.ringWidth;
}

/** cell 中心高度。 */
export function layerHeight(config: SpatialMapConfig, layer: BuildLayer, ring: number): number {
  if (layer === 'floor') return config.floorBaseY;
  if (layer === 'subsurface') return config.floorBaseY - (ring + 1) * config.wallBandHeight;
  // wall：沿坑壁向上
  return config.floorBaseY + (ring + 0.5) * config.wallBandHeight;
}

/** Build Cell → 世界坐标（cell 中心）。 */
export function cellToWorld(config: SpatialMapConfig, id: BuildCellId): WorldPoint {
  const angle = sectorAngle(config, id.sector, id.layer, id.ring);
  if (id.layer === 'wall') {
    // 坑壁：半径从 floorRadius 向外 + ring 推进，高度上升
    const radius = config.floorRadius + (id.ring + 0.5) * config.ringWidth;
    return {
      x: Math.cos(angle) * radius,
      y: layerHeight(config, id.layer, id.ring),
      z: Math.sin(angle) * radius,
    };
  }
  const radius = ringRadius(config, id.ring);
  return {
    x: Math.cos(angle) * radius,
    y: layerHeight(config, id.layer, id.ring),
    z: Math.sin(angle) * radius,
  };
}

/** 世界坐标 → floor/subsurface 极坐标 cell（不含 y 判层，由调用方给层）。 */
export function worldToCellOnLayer(
  config: SpatialMapConfig,
  point: WorldPoint,
  layer: BuildLayer,
): BuildCellId | null {
  const radius = Math.hypot(point.x, point.z);
  let angle = Math.atan2(point.z, point.x);
  if (angle < 0) angle += TWO_PI;

  // 先定环 —— 扇区数依赖环，顺序不能反
  let ring: number;
  if (layer === 'wall') {
    const rel = radius - config.floorRadius;
    if (rel < 0) return null;
    ring = Math.floor(rel / config.ringWidth);
  } else {
    const r0 = config.ringRadius0 ?? config.ringWidth * 0.5;
    ring = Math.floor((radius - r0 + config.ringWidth * 0.5) / config.ringWidth);
  }
  if (ring < 0 || ring >= config.ringsPerLayer[layer]) return null;

  const n = sectorsOfRing(config, layer, ring);
  const sector = Math.floor((angle / TWO_PI) * n) % n;
  return { layer, ring, sector };
}

/** 世界坐标 → 最可能的 cell（按高度推断层）。 */
export function worldToCell(config: SpatialMapConfig, point: WorldPoint): BuildCellId | null {
  const radius = Math.hypot(point.x, point.z);
  if (point.y < config.floorBaseY - config.wallBandHeight * 0.5) {
    return worldToCellOnLayer(config, point, 'subsurface');
  }
  if (radius >= config.floorRadius) {
    return worldToCellOnLayer(config, point, 'wall');
  }
  return worldToCellOnLayer(config, point, 'floor');
}
